import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { API, OPERATION_NAMES, AuthContext } from "../App";
import { toast } from "sonner";
import { Warehouse, MagnifyingGlass, CaretDown, Trash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import React from "react";

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
  { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
  { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
];

function StockAtSupplier() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [parts, setParts] = useState([]);

  // Filters
  const now = new Date();
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterProcess, setFilterProcess] = useState("");
  const [filterPartNo, setFilterPartNo] = useState("");
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());

  // Search popovers
  const [supplierSearchOpen, setSupplierSearchOpen] = useState(false);
  const [partSearchOpen, setPartSearchOpen] = useState(false);

  // Delete with password
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/stock-at-supplier/delete`, {
        part_no: deleteTarget.part_no,
        supplier: deleteTarget.supplier,
        process: deleteTarget.process,
        password: deletePassword
      });
      toast.success("Stock entry deleted");
      setDeleteTarget(null);
      setDeletePassword("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSupplier) params.set("supplier", filterSupplier);
      if (filterProcess) params.set("process", filterProcess);
      if (filterPartNo) params.set("part_no", filterPartNo);
      if (filterMonth) params.set("month", filterMonth);
      if (filterYear) params.set("year", filterYear);
      const res = await axios.get(`${API}/stock-at-supplier?${params}`);
      setData(res.data);
    } catch { toast.error("Failed to load stock data"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {});
    axios.get(`${API}/parts`).then(r => setParts(r.data)).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [filterSupplier, filterProcess, filterPartNo, filterMonth, filterYear]);

  // Get unique processes from selected supplier
  const selectedSup = suppliers.find(s => s.name === filterSupplier);
  const supProcesses = selectedSup?.processes || [];

  const years = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) years.push(y);

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="stock-at-supplier-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Warehouse size={24} className="text-[#1D2951]" />
            <h1 className="text-xl font-bold text-[#1D2951]">Stock at Supplier — Monthly Report</h1>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 border border-[#E2E8F0]">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Supplier with search */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Supplier</Label>
              <Popover open={supplierSearchOpen} onOpenChange={setSupplierSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between min-h-[40px] text-xs" data-testid="filter-supplier">
                    {filterSupplier || "All Suppliers"} <CaretDown size={12} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0">
                  <Command>
                    <CommandInput placeholder="Search supplier..." />
                    <CommandList>
                      <CommandEmpty>No supplier found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterSupplier(""); setSupplierSearchOpen(false); }}>All Suppliers</CommandItem>
                        {suppliers.map(s => (
                          <CommandItem key={s.id} onSelect={() => { setFilterSupplier(s.name); setSupplierSearchOpen(false); }}>
                            {s.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Process */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Process</Label>
              <Select value={filterProcess} onValueChange={v => setFilterProcess(v === "all" ? "" : v)}>
                <SelectTrigger className="min-h-[40px] text-xs" data-testid="filter-process"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Processes</SelectItem>
                  {(supProcesses.length > 0 ? supProcesses : Object.keys(OPERATION_NAMES)).map(p => (
                    <SelectItem key={p} value={p}>{OPERATION_NAMES[p] || p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Part No with search */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Part No</Label>
              <Popover open={partSearchOpen} onOpenChange={setPartSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between min-h-[40px] text-xs" data-testid="filter-part-no">
                    {filterPartNo || "All Parts"} <CaretDown size={12} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0">
                  <Command>
                    <CommandInput placeholder="Search part..." />
                    <CommandList>
                      <CommandEmpty>No part found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterPartNo(""); setPartSearchOpen(false); }}>All Parts</CommandItem>
                        {parts.map(p => (
                          <CommandItem key={p.id} onSelect={() => { setFilterPartNo(p.part_no); setPartSearchOpen(false); }}>
                            {p.part_no}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Month */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Month</Label>
              <Select value={String(filterMonth)} onValueChange={v => setFilterMonth(parseInt(v))}>
                <SelectTrigger className="min-h-[40px] text-xs" data-testid="filter-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Year */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Year</Label>
              <Select value={String(filterYear)} onValueChange={v => setFilterYear(parseInt(v))}>
                <SelectTrigger className="min-h-[40px] text-xs" data-testid="filter-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Results Table */}
        <Card className="border border-[#E2E8F0]">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="stock-supplier-table">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Supplier</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Process</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Part No</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">Open Stock</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">Cumulative Issued</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">Total Received</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase" title="Stock at Supplier = Open Stock + Received − Issued">Stock at Supplier</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-sm text-gray-500">Loading...</td></tr>
                ) : data.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-sm text-gray-500">No data for selected filters</td></tr>
                ) : data.map((d, i) => (
                  <tr key={i} className={d.stock_at_supplier > 0 ? "bg-[#FEF3C7]" : ""}>
                    <td className="px-4 py-3 text-sm font-semibold">{d.supplier || "-"}</td>
                    <td className="px-4 py-3 text-sm">{OPERATION_NAMES[d.process] || d.process || "-"}</td>
                    <td className="px-4 py-3 font-mono font-bold text-sm">{d.part_no}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{d.open_stock}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{d.cumulative_issued}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-[#059669]">{d.total_received}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-bold">{d.stock_at_supplier}</td>
                    <td className="px-4 py-3 text-center">
                      <Button variant="ghost" size="sm" className="text-[#EF4444] hover:bg-[#FEE2E2] h-8 w-8 p-0"
                        data-testid={`delete-stock-${i}`}
                        onClick={() => setDeleteTarget(d)}>
                        <Trash size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Dialog open={!!deleteTarget} onOpenChange={v => { if (!v) { setDeleteTarget(null); setDeletePassword(""); } }}>
          <DialogContent className="max-w-sm" data-testid="delete-password-dialog">
            <DialogHeader><DialogTitle className="text-[#EF4444]">Delete Stock Entry</DialogTitle></DialogHeader>
            {deleteTarget && (
              <div className="space-y-3">
                <p className="text-sm text-[#64748B]">
                  Deleting <span className="font-bold">{deleteTarget.part_no}</span> — {deleteTarget.supplier} / {OPERATION_NAMES[deleteTarget.process] || deleteTarget.process}
                </p>
                <p className="text-xs text-[#EF4444]">This will remove the Item Master config and all outsource transactions for this entry.</p>
                <div>
                  <Label className="text-xs font-semibold">Enter Password to Delete</Label>
                  <Input type="password" placeholder="Password" value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleDelete()}
                    data-testid="delete-password-input" />
                </div>
                <div className="flex gap-3">
                  <Button onClick={handleDelete} disabled={deleting || !deletePassword}
                    className="flex-1 bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="confirm-delete-btn">
                    {deleting ? "Deleting..." : "Delete"}
                  </Button>
                  <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeletePassword(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default StockAtSupplier;
